import { type PropsWithChildren, type FunctionComponent } from 'react';
import type { RouteConfig } from '../../../../utils/routes/configure-route';
import { AnalyticsContextProvider } from '../../../contexts/analytics-context/analytics.context';
import { useSetRumViewName } from '../../../hooks/datadog/rum.hooks';
import { AuthenticatedRoute } from '../../authenticated-route/authenticated-route.component';
import { EmployeeOnlyRoute } from '../../employee-only-route/employee-only-route.component';
import { LayoutController } from '../layout.controller';

const RouteOrAuthRoute: FunctionComponent<PropsWithChildren<RouteConfig>> = ({
	requiresAuthentication,
	requiresAccount,
	children,
	requiresEmployeeAuthentication,
	...remainingProps
}) => {
	const isSensativeRoute = requiresAccount || requiresAuthentication;
	const isSoftLogin = requiresAccount && !requiresAuthentication;

	if (isSensativeRoute) {
		return (
			<AuthenticatedRoute isSoftLogin={isSoftLogin} {...remainingProps}>
				{children}
			</AuthenticatedRoute>
		);
	} else if (requiresEmployeeAuthentication) {
		return <EmployeeOnlyRoute {...remainingProps}>{children}</EmployeeOnlyRoute>;
	} else {
		return children;
	}
};

export const RouteWithLayout: FunctionComponent<RouteConfig> = ({ layout, component, ...remainingProps }) => {
	const { ddRum, path, backgroundStyle } = remainingProps;
	const viewName = ddRum?.rumViewName || path;
	useSetRumViewName(ddRum?.rumViewNameSetByChild ? { rumViewName: viewName, rumViewNameSetByChild: true } : viewName);
	if (component) {
		// Type Juggling to make component types match
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const Component: FunctionComponent = component as FunctionComponent;
		return (
			<AnalyticsContextProvider>
				<RouteOrAuthRoute {...remainingProps}>
					<LayoutController layoutType={layout} backgroundStyle={backgroundStyle}>
						<Component />
					</LayoutController>
				</RouteOrAuthRoute>
			</AnalyticsContextProvider>
		);
	} else {
		return null;
	}
};
